// Query catalog — frontend invoke wrappers + types matching
// src-tauri/src/commands/query_catalog.rs.

export type ChartType = "bar" | "line" | "donut";

export interface ChartSpec {
  type: ChartType;
  x_col?: string;
  y_col?: string;
  x_label?: string;
  y_label?: string;
  y_suffix?: string;
  label_col?: string;
  value_col?: string;
}

export interface SampleResult {
  columns: string[];
  rows: (string | number | null)[][];
}

export interface CatalogEntry {
  id: string;
  hint: string;
  title: string;
  subtitle?: string;
  source_id: string | null;
  database_id: number | null;
  sql: string | null;
  chart: ChartSpec;
  sample_result: SampleResult;
}

export interface CatalogBundle {
  title: string;
  subtitle: string;
  queries: CatalogEntry[];
}

export interface QueryRunResult {
  status: "live" | "sample" | "error";
  columns: string[];
  rows: (string | number | null)[][];
  elapsed_ms: number;
  source_label: string;
  error: string | null;
}

const hasTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

// Browser build (Cloudflare Pages) reads from the metabase-proxy Worker
// instead of going through Rust. `VITE_METABASE_PROXY_URL` is set at
// build time by `npm run build:web`. If it's not set we fall back to
// the inline DEV_CATALOG so `npm run dev` keeps working unchanged.
const PROXY_URL = readEnv("VITE_METABASE_PROXY_URL").replace(/\/$/, "");

function readEnv(name: string): string {
  if (typeof import.meta === "undefined") return "";
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return (env && typeof env[name] === "string" ? env[name]! : "") || "";
}

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const mod = await import("@tauri-apps/api/core");
  return mod.invoke<T>(cmd, args);
}

// Bearer token the proxy expects — same passphrase the AuthGate checks
// at sign-in. Reading it lazily (instead of importing PASSPHRASE at the
// top) keeps this file usable from build-time tools that don't load
// the React config tree.
async function proxyBearer(): Promise<string> {
  const cfg = await import("../config/access");
  return cfg.PASSPHRASE;
}

async function proxyFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const bearer = await proxyBearer();
  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${bearer}`);
  headers.set("content-type", "application/json");
  const resp = await fetch(`${PROXY_URL}${path}`, { ...init, headers });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`proxy ${resp.status}: ${text.slice(0, 200)}`);
  }
  return (await resp.json()) as T;
}

// Browser-dev fallback catalog — small set so the Vite dev server renders
// without the Tauri shell. Kept inline to avoid bundling the full JSON twice.
const DEV_CATALOG: CatalogBundle = {
  title: "Bvndle data oracle",
  subtitle: "Browser-dev fallback · Tauri shell ships the full catalog.",
  queries: [
    {
      id: "age-top-redeemers",
      hint: "age of the most active bvndle redeemers",
      title: "Age cohort × habit redeemers",
      source_id: null,
      database_id: null,
      sql: null,
      chart: { type: "bar", x_col: "age_bucket", y_col: "users", y_label: "Habit redeemers" },
      sample_result: {
        columns: ["age_bucket", "users"],
        rows: [
          ["18-24", 88],
          ["25-29", 139],
          ["30-34", 77],
          ["35-44", 71],
          ["45-54", 26],
          ["55+", 6],
        ],
      },
    },
    // Bound by the seven KPIs (see lib/kpis.ts). Sample rows mirror the full
    // catalog (resources/cohorts/query-catalog.json) so the Command Centre
    // shows numbers in plain browser dev; the Tauri shell serves live data.
    {
      id: "merchant-coin-issue",
      hint: "which merchant issued the most coins",
      title: "Top redeem destinations by coins · 90d",
      source_id: null,
      database_id: 133,
      sql: null,
      chart: { type: "bar", x_col: "category", y_col: "coins", y_label: "Coins issued (90d)" },
      sample_result: {
        columns: ["category", "coins"],
        rows: [
          ["MTN NG (airtime)", 482914],
          ["Naira transfer fee", 312804],
          ["Airtel NG (airtime)", 188421],
          ["Card transactions", 142210],
          ["Glo NG (airtime)", 96833],
          ["Bill payments", 64118],
          ["9mobile NG (airtime)", 41207],
          ["Gift cards", 22650],
        ],
      },
    },
    {
      id: "redeem-category-share",
      hint: "what users actually do with their coins",
      title: "First-redeem category share",
      source_id: null,
      database_id: 133,
      sql: null,
      chart: { type: "donut", label_col: "category", value_col: "n", y_label: "First redemptions" },
      sample_result: {
        columns: ["category", "n"],
        rows: [
          ["Naira transfer fee", 21327],
          ["Airtime · MTN", 3144],
          ["Card transactions", 1841],
          ["Tag-to-Tag", 1402],
          ["Bvndle Games", 901],
          ["Other", 551],
        ],
      },
    },
    {
      id: "annual-redemption-volume",
      hint: "annualised redemption volume",
      title: "Annualised redemption volume · 365d",
      source_id: null,
      database_id: 133,
      sql: null,
      chart: { type: "bar", x_col: "window", y_col: "redemptions", y_label: "Redemptions" },
      sample_result: {
        columns: ["window", "redemptions"],
        rows: [
          ["Annual 365d", 412034],
          ["Monthly avg", 34336],
          ["Daily avg", 1129],
        ],
      },
    },
    {
      id: "habit-window-distribution",
      hint: "how fast do users come back for a second redeem",
      title: "Habit window · days from 1st to 2nd redeem",
      source_id: null,
      database_id: 133,
      sql: null,
      chart: { type: "bar", x_col: "bucket", y_col: "users", y_label: "Users in bucket" },
      sample_result: {
        columns: ["bucket", "users"],
        rows: [
          ["Same day", 6758],
          ["1-3 days", 2847],
          ["3-7 days", 2360],
          ["7-14 days", 1908],
          ["14-30 days", 2057],
          ["30-60 days", 1679],
          ["60-90 days", 936],
          ["90+ days", 1307],
        ],
      },
    },
  ],
};

export async function listCatalogQueries(): Promise<CatalogBundle> {
  if (hasTauri) return invoke<CatalogBundle>("list_catalog_queries");
  if (PROXY_URL) {
    try {
      return await proxyFetch<CatalogBundle>("/list-queries");
    } catch (e) {
      console.warn("[query-catalog] proxy list-queries failed", e);
    }
  }
  return DEV_CATALOG;
}

export async function runCatalogQuery(entryId: string): Promise<QueryRunResult> {
  if (hasTauri) return invoke<QueryRunResult>("run_catalog_query", { entryId });
  if (PROXY_URL) {
    try {
      return await proxyFetch<QueryRunResult>("/run-query", {
        method: "POST",
        body: JSON.stringify({ entryId }),
      });
    } catch (e) {
      console.warn(`[query-catalog] proxy run-query(${entryId}) failed`, e);
    }
  }
  const entry = DEV_CATALOG.queries.find((q) => q.id === entryId);
  if (!entry) {
    return {
      status: "error",
      columns: [],
      rows: [],
      elapsed_ms: 0,
      source_label: "browser-dev",
      error: `no entry ${entryId} in dev catalog`,
    };
  }
  return {
    status: "sample",
    columns: entry.sample_result.columns,
    rows: entry.sample_result.rows,
    elapsed_ms: 8,
    source_label: "browser-dev oracle",
    error: null,
  };
}

/**
 * Pick the catalog entry whose hint best matches the user's free-text query.
 * Token-overlap scoring — good enough until we wire a real NL→SQL backend.
 */
export function nearestEntry(query: string, entries: CatalogEntry[]): CatalogEntry | null {
  const q = query.toLowerCase().trim();
  if (!q || entries.length === 0) return null;
  const qTokens = new Set(q.split(/\s+/).filter((t) => t.length > 2));
  if (qTokens.size === 0) return entries[0]!;

  let best: { score: number; entry: CatalogEntry } | null = null;
  for (const entry of entries) {
    const hintTokens = new Set(
      entry.hint
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 2),
    );
    let overlap = 0;
    qTokens.forEach((t) => {
      if (hintTokens.has(t)) overlap += 1;
    });
    if (!best || overlap > best.score) {
      best = { score: overlap, entry };
    }
  }
  return best?.entry ?? entries[0]!;
}
